import { useState } from 'react';
import { AlertCircle, AlertTriangle, ArrowRightLeft, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { showToast } from '@/components/ui/toaster';
import { WorkspaceChips } from '@/components/WorkspaceSwitcher';
import { useI18n } from '@/lib/i18n';
import { ATTENDANCE_PLUGIN, isOwnedBy, otherAttendanceWorkspaces } from '@/lib/workspace-select';
import { workspaceService } from '@/services/workspace.service';
import { useWorkspaceStore, type Workspace } from '@/stores/workspace.store';

/**
 * Layar "Absensi belum aktif" untuk workspace yang dipilih.
 *
 * Dulu: "Plugin belum aktif … Hubungi owner workspace untuk mengaktifkan" — tanpa
 * nama workspace & tanpa jalan pindah. Silvi (tiket Voiz, 4 Agu 2026) ternyata
 * sedang di workspace PRIBADINYA; karena dia owner-nya, Absensi dia aktifkan di
 * situ → absennya tercatat terpisah & kantor tidak pernah melihatnya. Sekarang:
 *  - nama workspace disebut;
 *  - workspace lain yang Absensinya AKTIF ditawarkan sebagai pindah sekali-tap;
 *  - owner diperingatkan jelas (absen di sini terpisah dari kantornya) SEBELUM
 *    ditawari mengaktifkan, dan mengaktifkan butuh konfirmasi kedua.
 */
export function AttendanceOffScreen({ workspace, workspaces, userId, onSwitch, onReload, reloading, reloadFailed }: {
    workspace: Workspace;
    workspaces: Workspace[];
    userId?: string | null;
    onSwitch: (ws: Workspace) => void;
    onReload: () => void;
    reloading?: boolean;
    /** Muat ulang daftar workspace gagal (server sibuk/offline) — beri tahu, jangan diam. */
    reloadFailed?: boolean;
}) {
    const { t } = useI18n();
    const others = otherAttendanceWorkspaces(workspaces, workspace._id, userId);
    // "Absensi KANTORMU sudah aktif di workspace lain" hanya benar kalau salah satunya
    // memang bukan milik user; kalau semuanya workspace miliknya sendiri, pakai kalimat umum.
    const hasCompanyOthers = others.some((w) => !isOwnedBy(w, userId));
    const owned = isOwnedBy(workspace, userId);
    const ownerName = workspace.owner?.name || workspace.owner?.email || '';

    return (
        <div className="px-4 pt-6 max-w-md mx-auto space-y-3">
            <div className="bg-white rounded-2xl p-5 text-center" style={{ border: '1px solid #e0d6cc' }}>
                <div className="h-12 w-12 mx-auto rounded-full flex items-center justify-center bg-amber-100 mb-3">
                    <AlertCircle className="h-6 w-6 text-amber-600" />
                </div>
                <p className="text-sm font-bold text-gray-800">{t('workspace.off_title')}</p>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                    {t('workspace.off_body_prefix')} <span className="font-bold text-gray-800">{workspace.name}</span>.
                </p>
                {!owned && (
                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">
                        {ownerName
                            ? t('workspace.off_ask_owner_named', { owner: ownerName })
                            : t('workspace.off_ask_owner')}
                    </p>
                )}
            </div>

            {others.length > 0 && (
                <div className="bg-white rounded-2xl p-4" style={{ border: '1px solid #e0d6cc' }}>
                    <p className="text-sm font-bold text-gray-800">{t('workspace.off_others_title')}</p>
                    <p className="text-[11px] text-gray-500 mt-0.5 mb-2.5 leading-snug">{t('workspace.off_others_hint')}</p>
                    <div className="space-y-2">
                        {others.map((ws) => (
                            <button
                                key={ws._id}
                                type="button"
                                onClick={() => onSwitch(ws)}
                                className="flex items-center gap-3 w-full px-3 py-2.5 rounded-xl text-left bg-brand-50 hover:bg-brand-100 transition"
                                style={{ border: '1px solid #d0e7cf' }}
                            >
                                <ArrowRightLeft className="h-4 w-4 shrink-0 text-brand-700" />
                                <span className="flex-1 min-w-0">
                                    <span className="block text-xs font-bold text-brand-900 truncate">
                                        {t('workspace.switch_to', { name: ws.name })}
                                    </span>
                                    <WorkspaceChips ws={ws} userId={userId} />
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {owned && <OwnerEnableCard workspace={workspace} hasCompanyOthers={hasCompanyOthers} />}

            {reloadFailed && !reloading && (
                <p role="alert" className="text-[11px] text-center text-red-600 leading-snug px-2">
                    {t('workspace.off_reload_failed')}
                </p>
            )}

            <button
                type="button"
                onClick={onReload}
                disabled={reloading}
                className="w-full flex items-center justify-center gap-2 text-xs font-bold text-gray-600 py-2.5 disabled:opacity-50"
            >
                <RefreshCw className={reloading ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} />
                {t('workspace.off_reload')}
            </button>
        </div>
    );
}

function OwnerEnableCard({ workspace, hasCompanyOthers }: { workspace: Workspace; hasCompanyOthers: boolean }) {
    const { t } = useI18n();
    const patchWorkspace = useWorkspaceStore((s) => s.patchWorkspace);
    const [confirming, setConfirming] = useState(false);
    const [saving, setSaving] = useState(false);

    const enable = async () => {
        setSaving(true);
        try {
            const res = await workspaceService.setPlugin(workspace._id, ATTENDANCE_PLUGIN, true);
            const fromServer = (res.data as any)?.enabledPlugins;
            const next: string[] = Array.isArray(fromServer)
                ? fromServer
                : Array.from(new Set([...(workspace.enabledPlugins ?? []), ATTENDANCE_PLUGIN]));
            showToast({ variant: 'success', description: t('workspace.enable_done', { name: workspace.name }) });
            patchWorkspace(workspace._id, { enabledPlugins: next });
        } catch (err: any) {
            showToast({ variant: 'destructive', description: err?.response?.data?.error || t('workspace.enable_failed') });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="rounded-2xl p-4" style={{ backgroundColor: '#fffbeb', border: '1px solid #fde68a' }} role="note">
            <div className="flex items-start gap-2.5">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-amber-700" />
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold text-amber-900">{t('workspace.owner_warn_title', { name: workspace.name })}</p>
                    <p className="text-[11px] text-amber-900 mt-1 leading-relaxed">{t('workspace.owner_warn_body')}</p>
                    <p className="text-[11px] text-amber-800 mt-1.5 leading-relaxed">{t('workspace.owner_warn_ok')}</p>
                </div>
            </div>

            {!confirming ? (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setConfirming(true)}
                    className="w-full mt-3 h-auto py-2 whitespace-normal"
                >
                    {t('workspace.enable_here', { name: workspace.name })}
                </Button>
            ) : (
                <div className="mt-3 rounded-xl bg-white p-3" style={{ border: '1px solid #fde68a' }}>
                    <p className="text-[11px] font-bold text-gray-800 leading-snug">
                        {t(hasCompanyOthers ? 'workspace.enable_confirm_others' : 'workspace.enable_confirm', { name: workspace.name })}
                    </p>
                    <div className="flex gap-2 mt-2.5">
                        <Button variant="outline" size="sm" className="flex-1" onClick={() => setConfirming(false)} disabled={saving}>
                            {t('common.cancel')}
                        </Button>
                        <Button size="sm" className="flex-1 h-auto py-2 whitespace-normal" onClick={() => { void enable(); }} disabled={saving}>
                            {saving ? t('common.processing') : t('workspace.enable_confirm_btn')}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
